import React, { useEffect, useRef, useState } from 'react';

import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { useHandleProfileData } from '@pkg/hooks';

import { validateUsername } from '@app/helpers';

import { handleEnter } from '../../../utils';
import { CharacterCount } from '../components/CharacterCount';
import { EditIcon } from '../components/EditIcon';

import { EditableArea, ErrorText, FieldHolder, Wrapper } from './styles';

const MIN = 8;
const MAX = 16;
const UNIQUENESS_ERROR = 'That username is already taken';

export const UserName = ({ username }: { username?: string | null }) => {
    const { t } = useTranslation('fantasy_wishlist');
    const { updateUsername, duplicateUsername } = useHandleProfileData();

    const [value, setValue] = useState('');
    const [valueLength, setValueLength] = useState(0);
    const [isEditing, setIsEditing] = useState(false);
    const [usernameError, setUsernameError] = useState('');
    const [showDuplicateError, setShowDuplicateError] = useState(false);
    const [hasChanged, setHasChanged] = useState(false);

    const usernameRef = useRef<HTMLDivElement>(null);

    useEffect(() => setValue(username || ''), [username]);
    useEffect(() => {
        setValueLength(value?.length || 0);
    }, [value]);
    useEffect(() => setShowDuplicateError(duplicateUsername === value), [duplicateUsername, value]);

    function handleError(value: string) {
        const messages = validateUsername(value);
        const error = messages.find((m) => m.length > 0) || '';
        setUsernameError(error);
    }

    const handleChange = (e: React.ChangeEvent<HTMLDivElement>) => {
        setValueLength(e.currentTarget.textContent?.length || 0);
        handleError(e.currentTarget.textContent || '');
        setHasChanged(true);
        e.preventDefault();
        e.stopPropagation();
    };

    const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
        const textValue = e.target.textContent;
        if (!textValue) return;
        const duplicateNotChanged = duplicateUsername === textValue;
        setIsEditing(false);
        setValue(textValue || '');
        setShowDuplicateError(duplicateNotChanged);
        const shouldUpdate =
            textValue?.length && !usernameError && !invalidCount && textValue !== username && !duplicateNotChanged;
        if (shouldUpdate) {
            updateUsername(textValue);
        }
    };

    const handleClick = () => {
        setShowDuplicateError(false);
        setIsEditing(true);
    };

    const handleKeyDownEnter = (e: React.KeyboardEvent<HTMLDivElement>) => {
        setIsEditing(false);
        setValue(e.currentTarget.textContent || '');
        usernameRef.current?.blur();
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        document.getSelection()?.getRangeAt(0).insertNode(document.createTextNode(text));
    };

    const invalidCount = valueLength < MIN || valueLength > MAX;
    const isInvalid = Boolean(invalidCount || usernameError?.length > 0) && hasChanged;

    return (
        <Wrapper>
            <FieldHolder>
                <EditableArea
                    ref={usernameRef}
                    contentEditable={'true'}
                    suppressContentEditableWarning={true}
                    onPointerDown={handleClick}
                    onBlur={handleBlur}
                    onKeyDown={(e) => handleEnter(e, () => handleKeyDownEnter(e))}
                    onPaste={handlePaste}
                    onChange={handleChange}
                    onInput={handleChange}
                    $isInvalid={isInvalid}
                    spellCheck={false}
                >
                    {value}
                    <EditIcon />
                </EditableArea>
                {(isEditing || invalidCount) && hasChanged && (
                    <CharacterCount count={valueLength} isInvalid={invalidCount} />
                )}
            </FieldHolder>
            <AnimatePresence>
                {(usernameError || showDuplicateError) && (
                    <ErrorText
                        initial={{ opacity: 0, height: 0, marginTop: 0 }}
                        animate={{ opacity: 1, height: 'auto', marginTop: 8 }}
                        exit={{ opacity: 0, height: 0, marginTop: 0 }}
                    >
                        {(showDuplicateError && UNIQUENESS_ERROR) || (usernameError && t(usernameError))}
                    </ErrorText>
                )}
            </AnimatePresence>
        </Wrapper>
    );
};
